<template>
  <AIVDialog
    v-model="dialogVisible"
    title="Email Thread"
    width="90%"
    height="85vh"
    class="email-thread-viewer"
  >
    <div class="thread-viewer-content">
      <p class="thread-subject">
        {{ notification.subject || notification.email_subject || "No Subject" }}
      </p>

      <!-- Thread Info -->
      <div class="thread-info">
        <div class="info-row">
          <span class="label">Client:</span>
          <span class="value">{{ notification.clientName }}</span>
        </div>
        <div class="info-row">
          <span class="label">Authorization:</span>
          <span class="value">{{ notification.authNumber }}</span>
        </div>
        <div class="info-row">
          <span class="label">Recipients:</span>
          <span class="value">{{ notification.recipients }}</span>
        </div>
        <div class="info-row">
          <span class="label">Status:</span>
          <span class="status-badge" :class="notification.status">
            {{ formatStatus(notification.status) }}
          </span>
        </div>
      </div>

      <!-- Original Email Sent -->
      <div v-if="originalEmailContent" class="original-email">
        <h4><i class="fas fa-paper-plane"></i> Original Email Sent</h4>
        <div class="email-message outbound">
          <div class="message-header">
            <div class="sender-info">
              <i class="fas fa-paper-plane"></i>
              <strong>{{ notification.sentBy || 'System' }}</strong>
              <span class="outbound-label">(Original)</span>
            </div>
            <div class="message-date">
              {{ formatDate(notification.sentAt) }}
            </div>
          </div>
          <div class="message-body">
            <div v-if="originalEmailIsHtml" v-html="sanitizeHtml(originalEmailContent)"></div>
            <div v-else class="text-content">{{ originalEmailContent }}</div>
          </div>
        </div>
      </div>

      <!-- Email Thread -->
      <div class="email-thread">
        <div v-if="loadingThread" class="loading">
          <i class="fas fa-spinner fa-spin"></i> Loading thread...
        </div>
        <div v-else-if="threadError" class="error-message">
          <i class="fas fa-exclamation-triangle"></i>
          {{ threadError }}
        </div>
        <div v-else-if="replies.length === 0" class="no-replies">
          <i class="fas fa-inbox"></i>
          <p>No replies yet</p>
        </div>
        <div v-else class="replies-list">
          <div
            v-for="reply in sortedReplies"
            :key="reply.id"
            class="email-message"
            :class="{ outbound: reply.is_outbound }"
          >
            <div class="message-header">
              <div class="sender-info">
                <i
                  class="fas"
                  :class="reply.is_outbound ? 'fa-reply' : 'fa-envelope-open'"
                ></i>
                <strong>{{ reply.from_name || reply.from_email }}</strong>
                <span v-if="reply.is_outbound" class="outbound-label"
                  >(You)</span
                >
              </div>
              <div class="message-date">
                {{ formatDate(reply.received_at) }}
              </div>
            </div>
            <div class="message-body">
              <div
                v-if="reply.html_content"
                v-html="sanitizeHtml(reply.html_content)"
              ></div>
              <div v-else class="text-content">{{ reply.text_content }}</div>
            </div>
            <div
              v-if="reply.attachments && reply.attachments.length > 0"
              class="attachments"
            >
              <div class="attachment-label">
                <i class="fas fa-paperclip"></i> Attachments:
              </div>
              <div
                v-for="(attachment, index) in reply.attachments"
                :key="index"
                class="attachment-item clickable"
                @click="downloadAttachment(reply, attachment, index)"
              >
                <i class="fas" :class="getAttachmentIcon(attachment.filename)"></i>
                {{ attachment.filename }}
                <span class="file-size"
                  >({{ formatFileSize(attachment.size) }})</span
                >
                <i class="fas fa-download download-icon"></i>
                <button
                  v-if="isPdf(attachment.filename)"
                  class="btn-upload-auth"
                  @click.stop="uploadToAuth(reply, attachment, index)"
                  title="Upload to Authorization"
                >
                  <i class="fas fa-file-upload"></i> Upload to Auth
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Reply Form -->
      <div v-if="showReplyForm" class="reply-section">
        <h4><i class="fas fa-reply"></i> Send Reply</h4>
        <div class="email-message outbound">
          <textarea
            v-model="replyContent"
            placeholder="Type your reply here..."
            rows="6"
            class="reply-textarea"
          ></textarea>
          <div class="form-actions">
            <button
              @click="sendReply"
              :disabled="!replyContent.trim() || sending"
              class="btn-primary"
            >
              <i
                class="fas"
                :class="sending ? 'fa-spinner fa-spin' : 'fa-paper-plane'"
              ></i>
              {{ sending ? "Sending..." : "Send Reply" }}
            </button>
            <button
              @click="showReplyForm = false"
              :disabled="sending"
              class="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
      <div v-else class="reply-toggle">
        <button @click="showReplyForm = true" class="btn-primary">
          <i class="fas fa-reply"></i>
          Reply to Email
        </button>
        <button @click="refreshThread" class="btn-secondary">
          <i class="fas fa-sync-alt"></i>
          Refresh
        </button>
      </div>
    </div>
  </AIVDialog>
</template>

<script>
import DOMPurify from "dompurify";
import { emailRepliesService } from "@/serviceHandlers/emailRepliesService";

export default {
  name: "EmailThreadViewer",
  props: {
    notification: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dialogVisible: true,
      replies: [],
      loadingThread: false,
      threadError: null,
      showReplyForm: false,
      replyContent: "",
      sending: false,
    };
  },
  computed: {
    sortedReplies() {
      return [...this.replies].sort(
        (a, b) => new Date(a.received_at) - new Date(b.received_at),
      );
    },
    originalEmailContent() {
      return this.notification.emailContent
        || this.notification.email_content
        || this.notification.body
        || this.notification.html_content
        || null;
    },
    originalEmailIsHtml() {
      const content = this.originalEmailContent;
      return content && /<[a-z][\s\S]*>/i.test(content);
    },
  },
  watch: {
    dialogVisible(val) {
      if (!val) {
        this.$emit("close");
      }
    },
  },
  mounted() {
    this.loadThread();
  },
  methods: {
    async loadThread() {
      this.loadingThread = true;
      this.threadError = null;
      try {
        const response = await emailRepliesService.getNotificationReplies(this.notification.id);
        if (response.success) {
          this.replies = response.replies.map(reply => ({
            ...reply,
            attachments: typeof reply.attachments === 'string'
              ? JSON.parse(reply.attachments)
              : reply.attachments,
          }));
        }
      } catch (error) {
        this.threadError = "Failed to load email thread";
      } finally {
        this.loadingThread = false;
      }
    },
    async refreshThread() {
      await this.loadThread();
      this.$notifySuccess("Thread refreshed");
    },
    async sendReply() {
      if (!this.replyContent.trim()) return;

      this.sending = true;
      try {
        const response = await emailRepliesService.sendReply(
          this.notification.id,
          {
            content: this.replyContent,
            htmlContent: this.convertToHtml(this.replyContent),
          },
        );
        if (response.success) {
          this.$notifySuccess("Reply sent successfully");
          this.replyContent = "";
          this.showReplyForm = false;
          await this.loadThread();
          this.$emit("reply-sent");
        }
      } catch (error) {
        this.$notifyError(
          "Failed to send reply: " +
            (error.response?.data?.error || error.message),
        );
      } finally {
        this.sending = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " at " + date.toLocaleTimeString();
    },
    formatStatus(status) {
      const statusMap = {
        sent: "Sent",
        awaiting_reply: "Awaiting Reply",
        reply_received: "Reply Received",
        completed: "Completed",
        failed: "Failed",
      };
      return statusMap[status] || status;
    },
    formatFileSize(bytes) {
      if (!bytes) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },
    sanitizeHtml(html) {
      return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
          "p",
          "br",
          "strong",
          "em",
          "u",
          "a",
          "ul",
          "ol",
          "li",
          "blockquote",
          "div",
          "span",
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
        ],
        ALLOWED_ATTR: ["href", "target", "style"],
      });
    },
    convertToHtml(text) {
      return text
        .split("\n")
        .map((line) => `<p>${line}</p>`)
        .join("");
    },
    async downloadAttachment(reply, attachment, index) {
      try {
        const attachmentId = attachment.attachmentId || attachment.id || index;
        const blob = await emailRepliesService.downloadAttachment(
          this.notification.id,
          reply.id,
          attachmentId,
        );
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = attachment.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$notifyError("Failed to download attachment");
      }
    },
    isPdf(filename) {
      return filename && filename.toLowerCase().endsWith(".pdf");
    },
    uploadToAuth(reply, attachment, index) {
      const attachmentId = attachment.attachmentId || attachment.id || index;
      const route = this.$router.resolve({
        name: "Authorizations",
        query: {
          uploadAttachment: "true",
          notificationId: this.notification.id,
          replyId: reply.id,
          attachmentId: attachmentId,
          filename: attachment.filename,
        },
      });
      window.open(route.href, '_blank');
    },
    getAttachmentIcon(filename) {
      if (!filename) return "fa-file";
      const ext = filename.split(".").pop().toLowerCase();
      const iconMap = {
        pdf: "fa-file-pdf",
        doc: "fa-file-word",
        docx: "fa-file-word",
        xls: "fa-file-excel",
        xlsx: "fa-file-excel",
        png: "fa-file-image",
        jpg: "fa-file-image",
        jpeg: "fa-file-image",
        gif: "fa-file-image",
        zip: "fa-file-archive",
        rar: "fa-file-archive",
        txt: "fa-file-alt",
        csv: "fa-file-csv",
      };
      return iconMap[ext] || "fa-file";
    },
  },
};
</script>

<style scoped>
.thread-viewer-content {
  color: #e0e0e0;
}

.thread-subject {
  margin: 0 0 15px 0;
  color: #999;
  font-size: 14px;
}

.thread-info {
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  padding: 8px 0;
  border-bottom: 1px solid #4a4a4a;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row .label {
  width: 120px;
  color: #999;
  font-weight: 500;
}

.info-row .value {
  flex: 1;
  color: #e0e0e0;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-badge.sent,
.status-badge.awaiting_reply {
  background: rgba(255, 152, 0, 0.2);
  color: #ff9800;
}

.status-badge.reply_received {
  background: rgba(33, 150, 243, 0.2);
  color: #2196f3;
}

.status-badge.completed {
  background: rgba(76, 175, 80, 0.2);
  color: #4caf50;
}

.original-email {
  margin-bottom: 20px;
}

.original-email h4 {
  color: #4caf50;
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
}

.original-email h4 i {
  margin-right: 8px;
}

.email-thread {
  margin-bottom: 20px;
}

.loading,
.no-replies,
.error-message {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.no-replies i,
.error-message i {
  font-size: 48px;
  margin-bottom: 15px;
  display: block;
}

.error-message {
  color: #f44336;
}

.replies-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.email-message {
  background: rgba(76, 175, 80, 0.05);
  border-radius: 4px;
  padding: 15px;
  border-left: 3px solid #2196f3;
}

.email-message.outbound {
  border-left-color: #4caf50;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #4a4a4a;
}

.sender-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e0e0e0;
}

.sender-info i {
  color: #2196f3;
}

.email-message.outbound .sender-info i {
  color: #4caf50;
}

.outbound-label {
  color: #4caf50;
  font-size: 12px;
  font-weight: 600;
}

.message-date {
  color: #999;
  font-size: 13px;
}

.message-body {
  color: #e0e0e0;
  line-height: 1.6;
}

.text-content {
  white-space: pre-wrap;
}

.attachments {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #4a4a4a;
}

.attachment-label {
  color: #999;
  font-size: 13px;
  margin-bottom: 8px;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: #2c2c2c;
  border-radius: 4px;
  margin-bottom: 5px;
  color: #e0e0e0;
}

.attachment-item.clickable {
  cursor: pointer;
  transition: background 0.2s;
}

.attachment-item.clickable:hover {
  background: #4a4a4a;
}

.attachment-item i {
  color: #2196f3;
}

.download-icon {
  color: #999 !important;
  font-size: 12px;
  margin-left: 8px;
}

.attachment-item.clickable:hover .download-icon {
  color: #2196f3 !important;
}

.file-size {
  color: #999;
  font-size: 12px;
  margin-left: auto;
}

.btn-upload-auth {
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  margin-left: 8px;
  white-space: nowrap;
  transition: background 0.2s;
}

.btn-upload-auth:hover {
  background: #388e3c;
}

.btn-upload-auth i {
  color: white !important;
  margin-right: 4px;
}

.reply-section {
  margin-bottom: 20px;
}

.reply-section h4 {
  color: #4caf50;
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
}

.reply-section h4 i {
  margin-right: 8px;
}

.reply-textarea {
  width: 100%;
  padding: 12px;
  background: #2c2c2c;
  border: 1px solid #4a4a4a;
  border-radius: 4px;
  color: #e0e0e0;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 15px;
  box-sizing: border-box;
}

.reply-textarea:focus {
  outline: none;
  border-color: #4caf50;
}

.form-actions {
  display: flex;
  gap: 10px;
}

.reply-toggle {
  display: flex;
  gap: 10px;
}

.btn-primary,
.btn-secondary {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-primary {
  background: #2196f3;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #1976d2;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: #424242;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #616161;
}
</style>
